import {createSelector} from '@reduxjs/toolkit';
import {DateTime} from 'luxon';
import {AppState} from 'store';

import {
  Reservation,
  ReservationState as State,
  ReservationStatus as Status,
} from 'models/Reservations';
import {ReservationState} from 'store/reservations/reservations.reducer';

const reservationsState = (state: AppState): ReservationState => {
  return state.reservations;
};

export const reservationsSelector = createSelector(
  reservationsState,
  state => state.reservations
);

const futureFilter = (reservation: Reservation) =>
  reservation.state === State.FUTURE;

export const futureReservationsSelector = createSelector(
  reservationsSelector,
  reservations => reservations?.filter(futureFilter)
);

const notCanceledFilter = (reservation: Reservation) =>
  reservation.status.toUpperCase() !== Status.CANCELLED;

export const notCanceledReservationsSelector = createSelector(
  reservationsSelector,
  reservations => reservations?.filter(notCanceledFilter)
);

export const futureNotCanceledReservationsSelector = createSelector(
  futureReservationsSelector,
  notCanceledReservationsSelector,
  (future, notCanceled) => future?.filter(f => notCanceled.includes(f))
);

const sortByCheckInDateAsc = (a: Reservation, b: Reservation) =>
  DateTime.fromISO(a.checkInDate, {zone: 'America/Chicago'}).millisecond -
  DateTime.fromISO(b.checkInDate, {zone: 'America/Chicago'}).millisecond;

export const soonestValidReservationsSelector = createSelector(
  futureNotCanceledReservationsSelector,
  reservations => reservations?.sort(sortByCheckInDateAsc)
);

export const threeSoonestValidReservationsSelector = createSelector(
  soonestValidReservationsSelector,
  reservations =>
    reservations
      ?.sort((a, b) => {
        const firstReservationDate = new Date(
          a.checkInDate?.replace(/-/g, '/')
        );
        const secondReservationDate = new Date(
          b.checkInDate?.replace(/-/g, '/')
        );
        return firstReservationDate.getTime() - secondReservationDate.getTime();
      })
      ?.slice(0, 3)
);

export const reservationsLoadingSelector = createSelector(
  reservationsState,
  state => state.loading
);

export const reservationsFailedSelector = createSelector(
  reservationsState,
  state => state.failed
);
